// Student-facing REST service for hallpass-backend's passes-api and (for
// destinations) schools-api (POST/GET /api/passes/*, GET
// /api/schools/:schoolId/destinations — see src/api/config.js's
// PASSES_API_BASE / SCHOOLS_API_BASE and src/api/http.js's passesApi() /
// schoolsApi()).
//
// Route/role reference (apps/passes-api/src/routes/passes.ts):
//   POST   /api/passes            any authenticated student — creates own pass (PENDING)
//   GET    /api/passes            any authenticated student — server scopes to studentId=self
//   GET    /api/passes/:id        any authenticated student — server scopes to studentId=self
//   POST   /api/passes/:id/return student owner OR TEACHER/ADMIN
//   POST   /api/passes/:id/cancel student owner, teacher, or admin
//   POST   /api/passes/:id/approve TEACHER+ only (requireMinRole) — not exported here
//   POST   /api/passes/:id/deny    TEACHER+ only (requireMinRole) — not exported here
//
// Route/role reference (apps/schools-api/src/routes/destination.ts):
//   GET    /api/schools/:schoolId/destinations any authenticated user in
//     that school (requireAuth + requireSchoolAccess — no role check, so
//     STUDENT passes) — soft-deleted destinations (deletedAt set) excluded
//
// Status contract (see apps/passes-api/src/schemas/passes.ts + packages/db
// prisma schema's PassStatus enum): PENDING, WAITING, ACTIVE, COMPLETED,
// CANCELLED, DENIED, EXPIRED. There is no "RETURNED" status — POST
// /:id/return transitions a pass to COMPLETED (the Socket.io event name
// 'pass:returned' still carries status: 'COMPLETED'; the event name and
// the status value are not the same string).
//
// Timestamps on pass objects (requestedAt, approvedAt, activatedAt,
// returnedAt, deniedAt, cancelledAt) are ISO-8601 strings from the JSON
// body — parse with `new Date(str)`, never `.toDate()` (that's a
// Firestore Timestamp method and does not exist on these plain strings).

import { passesApi, buildQuery } from './http';
import { listDestinations, getPolicy } from './schoolConfigApi';
import type { components } from './types.gen';

type PassResponse = components['schemas']['PassResponse'];
type PassCursorPage = components['schemas']['PassCursorPage'];
type CreatePassBody = components['schemas']['CreatePassBody'];

// destinationId may arrive from an HTML <select> as a string; it's Number()
// -coerced into CreatePassBody's `number` before the request. All fields are
// optional here so the historical no-arg `= {}` default is preserved (a
// missing destinationId coerces to NaN — see createPass's documented current
// behavior).
type CreatePassParams = {
  studentId?: number;
  destinationId?: number | string;
  note?: string;
};

// Mirrors the backend listPassesQuery (apps/passes-api/src/schemas/passes.ts).
type ListPassesParams = {
  status?: string | string[];
  studentId?: number;
  classSectionId?: number;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
};

export const PASS_STATUS = Object.freeze({
  PENDING: 'PENDING',
  WAITING: 'WAITING',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  DENIED: 'DENIED',
  EXPIRED: 'EXPIRED',
});

// PENDING/WAITING/ACTIVE — the statuses that mean "this pass is still
// open," i.e. it counts toward the one-active-pass-per-student rule and
// hasn't reached a terminal state. Backing listOpenPasses() below.
const OPEN_STATUSES = [
  PASS_STATUS.PENDING,
  PASS_STATUS.WAITING,
  PASS_STATUS.ACTIVE,
];

// Shared with schoolConfigApi.js — re-exported here so student-facing
// callers can keep importing destinations from studentPassesApi.js.
export { listDestinations };

/**
 * Create a pass. destinationId may arrive from an HTML <select> as a
 * string; the backend's createPassSchema validates it with
 * z.number().int().positive() (no string coercion), so it's coerced here.
 *
 * studentId is ignored server-side for STUDENT callers (the pass is always
 * created for the caller). For TEACHER+ callers it's required — the route
 * returns 400 "studentId is required" without it — and names the student
 * the pass is being created for.
 *
 * @param {{studentId?: number, destinationId: number|string, note?: string}} params
 * @returns {Promise<object>} the created pass (status PENDING, or WAITING if
 *   the destination is at capacity)
 * @throws {import('./http').ApiError} 422 "No active period" | "Pass limit
 *   reached" | "Destination not found"; 409 "Active pass already exists"
 */
export function createPass({
  studentId,
  destinationId,
  note,
}: CreatePassParams = {}): Promise<PassResponse> {
  const body: CreatePassBody = { destinationId: Number(destinationId) };
  if (studentId !== undefined) {
    body.studentId = studentId;
  }
  if (note !== undefined) {
    body.note = note;
  }
  return passesApi<PassResponse>('/api/passes', { method: 'POST', body });
}

/**
 * List the current student's passes (server always scopes to the caller —
 * any studentId query param would be ignored, so none is exposed here).
 * Cursor-paginated: pass the previous response's nextCursor to page.
 *
 * For TEACHER/ADMIN callers, the backend does not scope to the caller —
 * requireSchoolMembership + the route's role check (see
 * apps/passes-api/src/routes/passes.ts's GET /) return every pass in the
 * caller's school, so this same function also backs school-wide/date-range
 * queries (e.g. admin "today's activity" stats) for those roles.
 *
 * `from`/`to` are compared against requestedAt server-side
 * (listPassesQuery's z.coerce.date()); pass ISO-8601 strings.
 *
 * `studentId`/`classSectionId` narrow the result set server-side
 * (listPassesQuery's positiveIntId()). Both are ignored for STUDENT callers
 * (the route forces studentId=self), so they're only meaningful for
 * TEACHER/ADMIN callers.
 *
 * @param {{status?: string|string[], studentId?: number, classSectionId?: number, from?: string, to?: string, cursor?: string, limit?: number}} [params]
 * @returns {Promise<{data: object[], nextCursor: string|null}>}
 */
export function listPasses({
  status,
  studentId,
  classSectionId,
  from,
  to,
  cursor,
  limit,
}: ListPassesParams = {}): Promise<PassCursorPage> {
  return passesApi<PassCursorPage>(
    `/api/passes${buildQuery({ status, studentId, classSectionId, from, to, cursor, limit })}`
  );
}

/**
 * Convenience wrapper for the common "what's currently in flight" view.
 * The backend's listPassesQuery accepts a single comma-separated `status`
 * value and splits it server-side (apps/passes-api/src/schemas/passes.ts),
 * so this is one request, not one per status.
 *
 * @returns {Promise<{data: object[], nextCursor: string|null}>}
 */
export function listOpenPasses(): Promise<PassCursorPage> {
  return listPasses({ status: OPEN_STATUSES });
}

/**
 * Fetch a single pass. 404s if it doesn't exist or belongs to another
 * student (the backend scopes the lookup to the caller for STUDENT role).
 *
 * @param {number|string} passId
 * @returns {Promise<object>}
 */
export function getPass(passId: number | string): Promise<PassResponse> {
  return passesApi<PassResponse>(`/api/passes/${passId}`);
}

/**
 * Mark a pass returned (ACTIVE -> COMPLETED). Student may only return their
 * own pass.
 *
 * @param {number|string} passId
 * @returns {Promise<object>} the updated pass (status COMPLETED)
 * @throws {import('./http').ApiError} 409 "Pass must be ACTIVE to return" /
 *   "Pass is no longer ACTIVE"; 404 if not found or not owned by the caller
 */
export function returnPass(passId: number | string): Promise<PassResponse> {
  return passesApi<PassResponse>(`/api/passes/${passId}/return`, { method: 'POST' });
}

/**
 * Cancel a pass (PENDING or WAITING -> CANCELLED). Student may only cancel
 * their own pass.
 *
 * @param {number|string} passId
 * @returns {Promise<object>} the updated pass (status CANCELLED)
 * @throws {import('./http').ApiError} 409 "Pass must be PENDING or WAITING
 *   to cancel" / "Pass is no longer cancellable"; 404 if not found or not
 *   owned by the caller
 */
export function cancelPass(passId: number | string): Promise<PassResponse> {
  return passesApi<PassResponse>(`/api/passes/${passId}/cancel`, { method: 'POST' });
}

// Shared with schoolConfigApi.js's getPolicy — kept as getSchoolPolicy here
// for existing student-facing callers.
export const getSchoolPolicy = getPolicy;
